import { Component, Vue, Watch } from 'vue-property-decorator';
import { Route, NavigationGuardNext } from 'vue-router';
import {
  PROJECT_ASSESSMENT_STATUSES,
  PROJECT_ASSESSMENT_STATUSES as pas,
  PROJECT_TYPES,
  EVALUATION_STATUSES,
  MANAGER_STATUS,
  NOTIFICATION
} from '@/constants';
import {
  AssessmentPeriodDTO,
  HRMUserEngagementTimeSheetDTO,
  ProjectAssessmentDTO,
  ProjectDTO,
  ProjectManager,
  UserDTO,
  UserEngagementDTO
} from '@/models';
import { formatDateToDisplay } from '@/helpers';
import { fetchAssessmentPeriods, fetchHRMMemberTimeSheets } from '@/services';

import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import ProjectEvaluationHeader from '@/components/common/project-evaluation-header/ProjectEvaluationHeader.vue';

import EvaluationProcessBar from '@/components/pages/project-evaluation/evaluation-process-bar/EvaluationProcessBar.vue';
import ProjectAssessment from '@/components/pages/project-evaluation/project-assessment/ProjectAssessment.vue';
import ProjectInformation from '@/components/pages/project-evaluation/project-information/ProjectInformation.vue';
import ProjectPeerEvaluation from '@/components/pages/project-evaluation/project-peer-evaluation/ProjectPeerEvaluation.vue';
import ProjectTeamMember from '@/components/pages/project-evaluation/project-team-member/ProjectTeamMember.vue';
import HrmTimeSheetSummary from './hrm-time-sheet-summary/HrmTimeSheetSummary.vue';
import ManuallyInputSummary from './manually-input-summary/ManuallyInputSummary.vue';
import { remapHrmTimeSheets } from '@/helpers/hrm';
import { Getter } from '@/decorators';

interface FeatureFlags {
  [key: string]: {
    view: boolean;
    edit: boolean;
    adjust?: boolean;
  };
}

interface StatusLabel {
  isShown: boolean;
  text: string;
}

@Component({
  components: {
    ConfirmationDialog,
    EvaluationProcessBar,
    InlineLoader,
    HrmTimeSheetSummary,
    ManuallyInputSummary,
    ProjectAssessment,
    ProjectEvaluationHeader,
    ProjectInformation,
    ProjectPeerEvaluation,
    ProjectTeamMember
  }
})
export default class ProjectEvaluation extends Vue {
  @Getter('project/managers') projectManagers!: ProjectManager[];

  currentPeriodAssessment = 0;
  loading = false;
  currentUser: UserDTO = {} as UserDTO;
  assessmentPeriods: AssessmentPeriodDTO[] = [];
  selectedAssessmentPeriod: AssessmentPeriodDTO = {} as AssessmentPeriodDTO;
  evaluationConfirmationDialogShown = false;
  next: NavigationGuardNext;

  hrmTimeSheets: HRMUserEngagementTimeSheetDTO[] = [];

  private readonly features = {
    PROJECT_INFORMATION: 'PROJECT_INFORMATION',
    PROJECT_ASSESSMENT: 'PROJECT_ASSESSMENT',
    TEAM_MEMBER_EVALUATION: 'TEAM_MEMBER_EVALUATION',
    PEER_EVALUATION: 'PEER_EVALUATION'
  };

  private featureFlags: FeatureFlags = {
    [this.features.PROJECT_INFORMATION]: {
      view: true,
      edit: false
    },
    [this.features.TEAM_MEMBER_EVALUATION]: {
      view: false,
      edit: false,
      adjust: false
    },
    [this.features.PROJECT_ASSESSMENT]: {
      view: false,
      edit: false
    },
    [this.features.PEER_EVALUATION]: {
      view: false,
      edit: false
    }
  };

  get isIndividualTrackingProject(): boolean {
    return this.$store.getters['project/projectType'] === PROJECT_TYPES.INDIVIDUAL_TRACKING;
  }

  get projectInfo(): ProjectDTO {
    return this.$store.getters['project/project'];
  }

  get currentProjectAssessment(): ProjectAssessmentDTO {
    return this.$store.getters['project/projectAssessment'];
  }

  get projectAssessments(): ProjectAssessmentDTO[] {
    return this.$store.getters['project/projectAssessments'];
  }

  get userEngagements(): UserEngagementDTO[] {
    return this.$store.getters['project/userEngagements'];
  }

  get assessmentStatus(): string {
    return this.$store.getters['project/assessmentStatus'];
  }

  get isEvaluationTabsShown(): boolean {
    return this.assessmentStatus !== PROJECT_ASSESSMENT_STATUSES.PROJECT_CREATION;
  }

  get assessmentPeriod(): string {
    const { selectedAssessmentPeriod } = this;
    return `${formatDateToDisplay(selectedAssessmentPeriod.startDate)} - ${formatDateToDisplay(
      selectedAssessmentPeriod.endDate
    )}`;
  }

  get isResultRelease(): boolean {
    return this.assessmentStatus === PROJECT_ASSESSMENT_STATUSES.RESULT_RELEASE;
  }

  get isIncludedInMemberList(): boolean {
    return this.userEngagements.some(ue => ue.user.id === this.currentUser.id);
  }

  get currentUserEngagement(): UserEngagementDTO {
    return this.userEngagements.find(ue => ue.user.id === this.currentUser.id) || ({} as UserEngagementDTO);
  }

  get isPeerEvaluation(): boolean {
    return this.assessmentStatus === PROJECT_ASSESSMENT_STATUSES.PEER_EVALUATION;
  }

  get isAppAdmin(): boolean | undefined {
    return this.currentUser.isAdmin;
  }

  get isSponsor(): boolean {
    return this.currentUser.id === this.projectInfo.sponsor.id;
  }

  get isConfirmShown(): boolean {
    return this.isAppAdmin || this.isSponsor;
  }

  get isManager(): boolean {
    const userId = this.currentUser.id;
    const projectManagerIds = (this.projectManagers || []).map(manager => manager.user.id);
    return userId === this.projectInfo.manager.id || projectManagerIds.includes(userId);
  }

  get isApproveDisabled(): boolean {
    const manager = this.projectManagers.find(manager => manager.user.id === this.currentUser.id);
    return manager ? manager.status === MANAGER_STATUS.APPROVED : false;
  }

  get statusLabelInsteadPeerEvaluation(): StatusLabel {
    if (this.isAppAdmin || this.isSponsor || this.isManager) {
      return {
        isShown: false,
        text: ''
      };
    }
    if (!this.isIncludedInMemberList) {
      return {
        isShown: true,
        text: 'You are not a member of the project in this period assessment.'
      };
    }
    if (this.isIndividualTrackingProject && !this.isResultRelease) {
      return {
        isShown: true,
        text: 'This project is being evaluated.'
      };
    }
    if (!this.isPeerEvaluation && !this.isResultRelease) {
      return {
        isShown: true,
        text: 'This project is being evaluated. Please wait until the Peer Evaluation step.'
      };
    }

    return {
      isShown: false,
      text: ''
    };
  }

  getAssessmentName(assessmentId: number): string {
    return this.assessmentPeriods.find(ap => ap.id === assessmentId)?.name || '';
  }

  navigateToNotFoundPage(): void {
    this.$router.push('/not-found');
  }

  @Watch('selectedAssessmentPeriod')
  async onCurrentPeriodAssessmentChange(): Promise<void> {
    const { selectedAssessmentPeriod } = this;
    if (selectedAssessmentPeriod) {
      await this.$store.dispatch('project/fetchProjectManagers', {
        projectId: this.projectInfo.id,
        assessmentPeriodId: selectedAssessmentPeriod.id
      });
    }
  }

  @Watch('projectManagers')
  onProjectManagersChange(): void {
    this.setFeatureFlags();
  }

  async created(): Promise<void> {
    this.loading = true;
    try {
      const { id } = this.$route.params;
      await this.$store.dispatch('project/asyncFetchProjectById', id);
      if (!this.projectInfo || !this.projectInfo.isActive) {
        this.navigateToNotFoundPage();
      }
      await this.$store.dispatch('project/asyncGetAverageSatAndComplexity');
      this.$store.commit('project/setIsProjectEditing', false);

      await this.$store.dispatch('user/asyncFetchUsers');
      this.currentUser = this.$store.getters['app/currentUser'];

      const { data } = await fetchAssessmentPeriods();
      this.assessmentPeriods = data || [];

      this.currentPeriodAssessment = this.getCurrentPeriodAssessment();
      await this.$store.dispatch('project/fetchProjectManagers', {
        projectId: this.projectInfo.id,
        assessmentPeriodId: this.currentPeriodAssessment
      });

      this.setFeatureFlags();
      const editedHRMTimeSheets = await this.$store.dispatch(
        'project/fetchEditedHRMTimeSheets',
        this.currentProjectAssessment?.id
      );

      const { data: hrmTimeSheet } = await fetchHRMMemberTimeSheets(this.currentProjectAssessment?.id);
      this.hrmTimeSheets = remapHrmTimeSheets(hrmTimeSheet, editedHRMTimeSheets);
    } catch {
      this.navigateToNotFoundPage();
    } finally {
      this.loading = false;
    }
  }

  setInitialSelectedPA(assessmentPeriodId: number): void {
    this.$store.commit('project/setSelectedProjectAssessmentId', assessmentPeriodId);
    const selectedAssessmentPeriod = this.assessmentPeriods.find(ap => ap.id === assessmentPeriodId);
    if (selectedAssessmentPeriod) {
      this.selectedAssessmentPeriod = selectedAssessmentPeriod;
    }
  }

  setFeatureFlags(): void {
    const isAppAdmin = this.isAppAdmin;
    const isSponsor = this.isSponsor;
    const isManager = this.isManager;
    const isProjectAdmin = isSponsor || isManager;

    const step = this.assessmentStatus;
    const isCreationStep = step === pas.PROJECT_CREATION;

    const peerEvaluationView1 = step === pas.PEER_EVALUATION && this.isIncludedInMemberList && !isProjectAdmin;
    const peerEvaluationView2 =
      (step === pas.PEER_EVALUATION || step === pas.RESULT_RELEASE) && this.isIncludedInMemberList;

    this.featureFlags = {
      [this.features.PROJECT_INFORMATION]: {
        view: true,
        edit: this.projectInfo.projectStatus === EVALUATION_STATUSES.NOT_STARTED.label && (isAppAdmin || isProjectAdmin)
      },
      [this.features.TEAM_MEMBER_EVALUATION]: {
        view: !isCreationStep && (isAppAdmin || isProjectAdmin || isManager),
        edit: step === pas.MEMBER_CONTRIBUTION_EVALUATION && (isAppAdmin || isManager),
        adjust: step === pas.PEER_EVALUATION && (isAppAdmin || isManager)
      },
      [this.features.PROJECT_ASSESSMENT]: {
        view:
          (step === pas.PROJECT_EVALUATION || step === pas.PEER_EVALUATION || step === pas.RESULT_RELEASE) &&
          (isAppAdmin || isProjectAdmin) &&
          !this.isIndividualTrackingProject,
        edit: step === pas.PROJECT_EVALUATION && (isAppAdmin || isSponsor)
      },
      [this.features.PEER_EVALUATION]: {
        view: peerEvaluationView1 || peerEvaluationView2,
        edit: step === pas.PEER_EVALUATION && this.isIncludedInMemberList
      }
    };
  }

  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext): void {
    if (this.$store.getters['project/isProjectEditing']) {
      this.evaluationConfirmationDialogShown = true;
      this.next = next;
    } else {
      next();
    }
  }

  onStepConfirmed(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.setFeatureFlags();
  }

  onLeaveDialogConfirm(): void {
    this.next();
  }

  onAssessmentSelected(id: number): void {
    this.$store.commit('project/setSelectedProjectAssessmentId', id);
    this.selectedAssessmentPeriod = this.assessmentPeriods.find(ap => ap.id === id) || ({} as AssessmentPeriodDTO);
    this.setFeatureFlags();
  }

  async onManagerApprove(): Promise<void> {
    const managerStatusUpdate = {
      managerId: this.currentUser.id,
      status: MANAGER_STATUS.APPROVED,
      projectId: this.projectInfo.id,
      assessmentPeriodId: this.selectedAssessmentPeriod.id
    };

    const response = await this.$store.dispatch('project/updateManagerStatus', managerStatusUpdate);
    if (response) {
      const updateManagersSuccess = {
        showing: true,
        type: NOTIFICATION.TYPE.success,
        title: NOTIFICATION.TEXT.approveSuccess,
        content: [],
        icon: NOTIFICATION.ICON.success,
        multiline: false
      };
      this.$store.commit('notification/setNotification', updateManagersSuccess);
    }
  }

  getCurrentPeriodAssessment(): number {
    const { assessmentPeriodId } = this.$route.params;
    const projectAssessments = this.projectAssessments;
    if (assessmentPeriodId) {
      this.setInitialSelectedPA(parseInt(assessmentPeriodId));
      const currentProjectAssessmentIdx = projectAssessments.findIndex(
        pa => pa.assessmentPeriodId === parseInt(assessmentPeriodId)
      );
      if (currentProjectAssessmentIdx !== -1) {
        return currentProjectAssessmentIdx;
      }
    }

    if (projectAssessments.length > 0) {
      this.setInitialSelectedPA(projectAssessments[0].assessmentPeriodId);
    }
    return 0;
  }
}
